import { convertAriaDateToDate, convertDateToAriaDate } from './aria-date-conversion';

export interface DateValidationResult {
  validationMessage: string[];
  validityState: ValidityState;
}

interface ValidationProps {
  maxDate?: Date;
  minDate?: Date;
  required?: boolean;
}

/**
 * Validates a date value based on the provided validation props.
 * @param value - The date value to be validated.
 * @param props - The validation props containing minDate, maxDate, and required.
 * @param locale - The locale string used for formatting date strings.
 * @returns An object containing the validation message and validity state.
 */
export function getDateValidation(value: Date | null, props: ValidationProps, locale?: string): DateValidationResult {
  const { maxDate, minDate, required } = props;

  // Cut off the time part of the value, max/min dates
  const onlyDateValue = convertAriaDateToDate(convertDateToAriaDate(value));
  const onlyDateMinDate = convertAriaDateToDate(convertDateToAriaDate(minDate));
  const onlyDateMaxDate = convertAriaDateToDate(convertDateToAriaDate(maxDate));

  const errors: string[] = [];

  const rangeOverflow = onlyDateValue && onlyDateMaxDate && onlyDateValue.getTime() > onlyDateMaxDate.getTime();
  const rangeUnderflow = onlyDateValue && onlyDateMinDate && onlyDateValue.getTime() < onlyDateMinDate.getTime();
  const valueMissing = required && onlyDateValue === null;

  const invalid = rangeOverflow || rangeUnderflow || valueMissing;

  if (invalid) {
    if (rangeOverflow) {
      errors.push(`Value must be ${maxDate?.toLocaleDateString(locale)} or earlier.`);
    }

    if (rangeUnderflow) {
      errors.push(`Value must be ${minDate?.toLocaleDateString(locale)} or later.`);
    }

    if (valueMissing) {
      errors.push('Value is required');
    }
  }

  return {
    validationMessage: errors,
    validityState: {
      badInput: false,
      customError: false,
      patternMismatch: false,
      rangeOverflow: !!rangeOverflow,
      rangeUnderflow: !!rangeUnderflow,
      stepMismatch: false,
      tooLong: false,
      tooShort: false,
      typeMismatch: false,
      valid: !invalid,
      valueMissing: !!valueMissing,
    },
  };
}
